<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar class="custom-toolbar">
        <ion-buttons slot="start">
          <ion-menu-button color="light"></ion-menu-button>
        </ion-buttons>
        <ion-title class="custom-title">Recall Leave</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="recall-content">
      <div class="recall-container">

        <!-- Search Mode Toggle -->
        <ion-segment v-model="searchMode" class="search-segment" @ionChange="onModeChange">
          <ion-segment-button value="date">
            <ion-label>By Date</ion-label>
          </ion-segment-button>
          <ion-segment-button value="employee">
            <ion-label>By Employee</ion-label>
          </ion-segment-button>
        </ion-segment>

        <!-- By Date search -->
        <div v-if="searchMode === 'date'" class="search-panel">
          <ion-item class="search-item" lines="none">
            <ion-icon :icon="calendarOutline" slot="start" color="primary"></ion-icon>
            <ion-label position="stacked">Who is on approved leave on:</ion-label>
            <ion-datetime
              display-format="YYYY-MM-DD"
              v-model="selectedDate"
              mode="ios"
              presentation="date"
              @ionChange="fetchByDate"
            ></ion-datetime>
          </ion-item>
        </div>

        <!-- By Employee search -->
        <div v-else class="search-panel">
          <ion-item class="search-item" lines="none">
            <ion-icon :icon="searchOutline" slot="start" color="primary"></ion-icon>
            <ion-input
              v-model="employeeName"
              placeholder="Search employee by name"
              @ionInput="onEmployeeSearchInput"
              class="custom-input"
            ></ion-input>
          </ion-item>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <ion-spinner name="crescent" color="primary"></ion-spinner>
          <span>Searching approved leaves...</span>
        </div>

        <!-- Results -->
        <div v-else-if="leaves.length > 0" class="leaves-list">
          <div v-for="leave in leaves" :key="leave.id" class="leave-card">
            <div class="card-accent"></div>

            <div class="card-header-row">
              <div class="employee-info">
                <div class="avatar-badge">
                  {{ getInitials(leave.user ? leave.user.name : 'Employee') }}
                </div>
                <div>
                  <h3 class="employee-name">{{ leave.user ? leave.user.name : 'Unknown Employee' }}</h3>
                  <span class="employee-sub">Approved Leave</span>
                </div>
              </div>
              <div class="leave-type-badge">
                <span>{{ leave.leave_type ? leave.leave_type.title : 'Leave' }}</span>
              </div>
            </div>

            <div class="card-body">
              <div class="dates-bento">
                <div class="date-col">
                  <span class="date-label">START DATE</span>
                  <span class="date-val">{{ formatDate(leave.start_date) }}</span>
                </div>
                <div class="duration-arrow">
                  <ion-icon :icon="arrowForwardOutline"></ion-icon>
                  <span class="duration-pill">{{ leave.num_of_days }} Days</span>
                </div>
                <div class="date-col">
                  <span class="date-label">END DATE</span>
                  <span class="date-val">{{ formatDate(leave.end_date) }}</span>
                </div>
              </div>

              <div class="reason-box">
                <span class="box-title">Reason for Leave:</span>
                <p class="reason-text">"{{ leave.reason || 'No reason provided' }}"</p>
              </div>

              <div v-if="leave.recalled_dates && leave.recalled_dates.length > 0" class="recalled-section">
                <span class="box-title">Already Recalled:</span>
                <div class="recalled-chips">
                  <span v-for="entry in leave.recalled_dates" :key="entry.date" class="recalled-chip">
                    {{ formatDate(entry.date) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="card-actions">
              <button class="btn-action btn-recall" @click="promptRecall(leave)">
                <ion-icon :icon="returnUpBackOutline"></ion-icon>
                <span>Recall</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-icon-wrapper">
            <ion-icon :icon="checkmarkDoneCircleOutline" class="empty-icon"></ion-icon>
          </div>
          <h2>Nothing to Recall</h2>
          <p v-if="searchMode === 'date'">No employees have approved leave on the selected date.</p>
          <p v-else>Search an employee by name to see their approved leaves.</p>
        </div>

      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/* eslint-disable */
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
  IonItem,
  IonLabel,
  IonInput,
  IonIcon,
  IonSpinner,
  IonDatetime,
  IonSegment,
  IonSegmentButton,
  alertController
} from '@ionic/vue';
import {
  calendarOutline,
  searchOutline,
  arrowForwardOutline,
  returnUpBackOutline,
  checkmarkDoneCircleOutline
} from 'ionicons/icons';
import Loading from '../../Loading';

export default {
  name: 'RecallLeave',
  components: {
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonItem,
    IonLabel,
    IonInput,
    IonIcon,
    IonSpinner,
    IonDatetime,
    IonSegment,
    IonSegmentButton
  },
  data() {
    return {
      calendarOutline,
      searchOutline,
      arrowForwardOutline,
      returnUpBackOutline,
      checkmarkDoneCircleOutline,

      searchMode: 'date',
      selectedDate: new Date().toISOString().split('T')[0],
      employeeName: '',
      employeeSearchTimer: null,
      leaves: [],
      loading: false
    }
  },
  mounted() {
    this.fetchByDate();
  },
  methods: {
    cleanDateStr(d) {
      if (!d) return '';
      return d.includes('T') ? d.split('T')[0] : d;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    },
    getInitials(name) {
      if (!name) return 'EE';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    async presentAlert(title, message) {
      const alert = await alertController.create({
        header: title,
        message: message,
        buttons: ['OK'],
      });
      await alert.present();
    },
    onModeChange() {
      this.leaves = [];
      if (this.searchMode === 'date') {
        this.fetchByDate();
      }
    },
    onEmployeeSearchInput() {
      clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => {
        this.fetchByEmployee();
      }, 400);
    },
    fetchByDate() {
      this.loading = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
        date: this.cleanDateStr(this.selectedDate)
      };

      this.$http.post('hr/searchApprovedLeaves', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.leaves = response.data.leaves;
          }
          this.loading = false;
        })
        .catch(err => {
          console.error(err);
          this.loading = false;
        });
    },
    fetchByEmployee() {
      if (!this.employeeName || !this.employeeName.trim()) {
        this.leaves = [];
        return;
      }
      this.loading = true;
      const data = {
        username: window.localStorage.username,
        password: window.localStorage.password,
        employee_name: this.employeeName.trim()
      };

      this.$http.post('hr/searchApprovedLeaves', data, { skipLoading: true })
        .then(response => {
          if (response.data.status) {
            this.leaves = response.data.leaves;
          }
          this.loading = false;
        })
        .catch(err => {
          console.error(err);
          this.loading = false;
        });
    },
    refreshResults() {
      if (this.searchMode === 'date') {
        this.fetchByDate();
      } else {
        this.fetchByEmployee();
      }
    },
    async promptRecall(leave) {
      const defaultDate = this.searchMode === 'date' ? this.cleanDateStr(this.selectedDate) : leave.start_date;
      const alert = await alertController.create({
        header: 'Recall ' + (leave.user ? leave.user.name : 'Employee'),
        message: 'Choose the date to recall and a reason. That date will no longer count as leave and 1 day will be refunded to their balance.',
        inputs: [
          {
            name: 'recall_date',
            type: 'date',
            value: defaultDate,
            min: leave.start_date,
            max: leave.end_date
          },
          {
            name: 'reason',
            type: 'text',
            placeholder: 'Reason for recall *'
          }
        ],
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Recall',
            handler: async (alertData) => {
              if (!alertData.recall_date) {
                this.presentAlert('Validation Error', 'Recall date is required.');
                return false;
              }
              if (!alertData.reason || !alertData.reason.trim()) {
                this.presentAlert('Validation Error', 'Reason for recall is required.');
                return false;
              }

              const loader = await Loading.presentLoading('Recalling...');
              const data = {
                username: window.localStorage.username,
                password: window.localStorage.password,
                requisition_id: leave.id,
                recall_date: alertData.recall_date,
                reason: alertData.reason
              };

              this.$http.post('hr/recallLeaveRequisition', data)
                .then(response => {
                  loader.dismiss();
                  if (response.data.status) {
                    this.presentAlert('Success', response.data.message);
                    this.refreshResults();
                  } else {
                    this.presentAlert('Failed', response.data.message);
                  }
                })
                .catch(err => {
                  loader.dismiss();
                });
            }
          }
        ]
      });
      await alert.present();
    }
  }
}
</script>

<style scoped>
.recall-content {
  --background: #f4f6fc;
}

.recall-container {
  padding: 16px;
  max-width: 600px;
  margin: 0 auto;
}

.custom-toolbar {
  --background: linear-gradient(135deg, #102a43 0%, #243b53 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-title {
  font-weight: 700;
  font-size: 1.15rem;
  letter-spacing: 0.5px;
}

.search-segment {
  margin-bottom: 16px;
}

.search-panel {
  background: #fff;
  border-radius: 16px;
  padding: 4px 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 16px;
}

.search-item {
  --background: transparent;
}

.custom-input {
  font-weight: 600;
  font-size: 0.95rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
  color: #627d98;
}

.leaves-list {
  padding-bottom: 8px;
}

.leave-card {
  position: relative;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.02);
  margin-bottom: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, #0072ff 0%, #00c6ff 100%);
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 12px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.2);
}

.employee-name {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #0f172a;
}

.employee-sub {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.leave-type-badge {
  background: #f1f5f9;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
}

.card-body {
  padding: 16px 16px 16px 20px;
}

.dates-bento {
  background: #f8fafc;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  margin-bottom: 14px;
}

.date-col {
  display: flex;
  flex-direction: column;
}

.date-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.date-val {
  font-size: 0.88rem;
  font-weight: 700;
  color: #334e68;
}

.duration-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #94a3b8;
}

.duration-arrow ion-icon {
  font-size: 1.1rem;
}

.duration-pill {
  background: #e2e8f0;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 8px;
}

.reason-box {
  margin-bottom: 10px;
}

.box-title {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 4px;
}

.reason-text {
  margin: 0;
  font-size: 0.88rem;
  color: #334e68;
  font-weight: 500;
  line-height: 1.45;
  background: #fafafa;
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 3px solid #cbd5e1;
}

.recalled-section {
  margin-top: 6px;
}

.recalled-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.recalled-chip {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
}

.card-actions {
  display: flex;
  gap: 10px;
  padding: 12px 16px 16px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.btn-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 12px;
  padding: 12px;
  font-size: 0.88rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: transform 0.15s;
}

.btn-action:active {
  transform: scale(0.97);
}

.btn-recall {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(234, 88, 12, 0.2);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  margin-top: 48px;
}

.empty-icon-wrapper {
  background: #d1fae5;
  padding: 20px;
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon {
  font-size: 3rem;
  color: #059669;
}

.empty-state h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #102a43;
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 0.88rem;
  color: #627d98;
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
}
</style>
